import { useState, useMemo } from "react";
import { useSystemPlans } from "@/hooks/use-plans";
import { useDeletePlan } from "@/hooks/use-delete-plan";
import { useConsolePlans } from "@/hooks/useConsolePlans";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Plus, 
  RefreshCw, 
  Edit, 
  Search,
  ChevronLeft,
  ChevronRight,
  Cloud,
  CloudOff,
  Loader2,
} from "lucide-react";
import { Plan } from "@/lib/plans/types";
import { CreatePlanModal } from "@/components/admin/CreatePlanModal";
import { EditPlanModal } from "@/components/admin/EditPlanModal";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface SystemPlansContentProps {
  systemCode: string;
  systemName: string;
}

function formatCurrency(value: number): string {
  if (value === 0) return "Sob consulta";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

function getVehicleLimit(plan: Plan): string {
  const limits = plan.features?.limits;
  if (!limits) return "-";
  
  const vehiclesMax = limits.fleet_vehicles_max || limits.vehicles_max || 0;
  if (vehiclesMax > 0 && vehiclesMax < 999999) {
    return `${vehiclesMax} veículo${vehiclesMax !== 1 ? 's' : ''}`;
  }
  
  return "Ilimitado";
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

interface PlanWithSync extends Plan {
  console_plan_id?: string | null;
  console_synced_at?: string | null;
  companies_count?: number;
}

export function SystemPlansContent({ systemCode, systemName }: SystemPlansContentProps) {
  const { data: plans = [], isLoading } = useSystemPlans(systemCode);
  const deletePlanMutation = useDeletePlan();
  const { loading: syncing, progress, isConfigured, syncAllPlans, syncSinglePlan } = useConsolePlans();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  // Count synced plans
  const syncedCount = useMemo(() => {
    return (plans as PlanWithSync[]).filter(p => p.console_plan_id).length;
  }, [plans]);

  // Filter plans by search query
  const filteredPlans = useMemo(() => {
    if (!searchQuery.trim()) return plans as PlanWithSync[];
    const query = searchQuery.toLowerCase();
    return (plans as PlanWithSync[]).filter(plan => 
      plan.name.toLowerCase().includes(query) ||
      plan.description?.toLowerCase().includes(query)
    );
  }, [plans, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredPlans.length / itemsPerPage);
  const paginatedPlans = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredPlans.slice(start, start + itemsPerPage);
  }, [filteredPlans, page, itemsPerPage]);

  // Reset page when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-48" />
            <div className="flex gap-2">
              <Skeleton className="h-9 w-32" />
              <Skeleton className="h-9 w-32" />
            </div>
          </div>
          <Skeleton className="h-10 w-full max-w-sm" />
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold">Gestão de Planos</h2>
            <p className="text-sm text-muted-foreground">
              Gerenciar planos de assinatura do {systemName}
              {isConfigured && (
                <span className="ml-2 text-xs">
                  • {syncedCount}/{plans.length} sincronizados
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={!isConfigured || syncing || plans.length === 0}
                    onClick={() => syncAllPlans(plans as PlanWithSync[], systemCode)}
                  >
                    {syncing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {progress.current}/{progress.total}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Sincronizar Todos
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {!isConfigured 
                    ? "Console API não configurada" 
                    : "Sincronizar todos os planos com a Console PortalONE"
                  }
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button 
              size="sm" 
              onClick={() => setCreateModalOpen(true)}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Plano
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plano</TableHead>
                <TableHead>Veículos</TableHead>
                <TableHead>Preço Mensal</TableHead>
                <TableHead>Empresas</TableHead>
                <TableHead>Console</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedPlans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    {plans.length === 0 
                      ? "Nenhum plano configurado ainda."
                      : "Nenhum plano encontrado com esses filtros."
                    }
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPlans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{plan.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {generateSlug(plan.name)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-medium">
                        {getVehicleLimit(plan)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">
                        {formatCurrency(plan.price_monthly)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {plan.companies_count ?? "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {plan.console_plan_id ? (
                              <Badge 
                                variant="outline" 
                                className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 cursor-help"
                              >
                                <Cloud className="h-3 w-3 mr-1" />
                                Sincronizado
                              </Badge>
                            ) : (
                              <Badge 
                                variant="outline" 
                                className="bg-muted text-muted-foreground cursor-help"
                              >
                                <CloudOff className="h-3 w-3 mr-1" />
                                Não sincronizado
                              </Badge>
                            )}
                          </TooltipTrigger>
                          <TooltipContent>
                            {plan.console_plan_id 
                              ? `Sincronizado em ${plan.console_synced_at ? format(new Date(plan.console_synced_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : '-'}`
                              : "Clique em ⟳ para sincronizar"
                            }
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={plan.is_active ? "default" : "secondary"}
                        className={plan.is_active 
                          ? "bg-green-500/20 text-green-400 hover:bg-green-500/30" 
                          : ""
                        }
                      >
                        {plan.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isConfigured && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="h-8 w-8"
                                  disabled={syncing}
                                  onClick={() => syncSinglePlan(plan, systemCode)}
                                >
                                  <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Sincronizar com Console</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setSelectedPlan(plan);
                            setEditModalOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Footer / Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Select
              value={itemsPerPage.toString()}
              onValueChange={(val) => {
                setItemsPerPage(parseInt(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <span>de {filteredPlans.length} registros</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {page} de {Math.max(1, totalPages)}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>

      {/* Modals */}
      <CreatePlanModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        systemCode={systemCode}
        systemName={systemName}
      />

      <EditPlanModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedPlan(null);
        }}
        plan={selectedPlan}
        systemCode={systemCode}
      />
    </Card>
  );
}
